/**
 * Numbers, in the shapes console/render.py states them.
 *
 * This is the one place the console formats anything, and it formats only NUMBERS. The sentences
 * around them are the server's. The shapes are copied from `render.py` so a figure drawn in a panel
 * and the same figure inside a `says` never differ by a decimal place — which they would the first
 * time someone chose `toLocaleString()` here and `:,.2f` there.
 *
 * `MINUS` is U+2212, not a hyphen, for the same reason the backend uses it: a hyphen in a column of
 * tabular figures is a different width from a digit, and the column stops lining up.
 */

const MINUS = "−";

/** `NT$10,011,200` · `−NT$38,000` — whole dollars, as the broker states them. */
export function nt(x: number | null | undefined): string {
  if (x === null || x === undefined) return "—";
  const value = Math.round(x);
  return `${value < 0 ? MINUS : ""}NT$${Math.abs(value).toLocaleString("en-US")}`;
}

/** `+NT$41,200` · `−NT$38,000` — a change says its sign. */
export function signedNt(x: number | null | undefined): string {
  if (x === null || x === undefined) return "—";
  const value = Math.round(x);
  return `${value >= 0 ? "+" : MINUS}NT$${Math.abs(value).toLocaleString("en-US")}`;
}

/** `+18.2%` from 0.182 — ratios travel as fractions, and are said signed. */
export function pct(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined) return "—";
  const sign = fraction >= 0 ? "+" : MINUS;
  return `${sign}${(Math.abs(fraction) * 100).toFixed(digits)}%`;
}

/**
 * `14.8%` — a *reading*, never negative, so unsigned. One decimal, because 15.0% and 14.8% both
 * printing "15%" is the largest holding unreadable.
 */
export function share(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined) return "—";
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** A fence value as its owner set it: `20%`, `0.5%` — never rounded to a number it isn't. */
export function rule(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return "—";
  const value = Math.round(fraction * 100 * 10_000) / 10_000;
  return `${value}%`;
}

export function usd(x: number | null | undefined, digits = 2): string {
  return `$${(x ?? 0).toFixed(digits)}`;
}

/** A price: two decimals, always. A column of `1,012`, `121.5` and `15.66` has no other structure
 *  to line it up. */
export function price(x: number | null | undefined): string {
  if (x === null || x === undefined) return "—";
  return `${x < 0 ? MINUS : ""}${Math.abs(x).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function shares(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US");
}

export function count(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined) return "—";
  return n.toFixed(digits);
}

/**
 * Which way a figure is painted — 漲紅跌綠, never inverted, and never `--danger`/`--accept`, which
 * are a kill switch and a confirmation and have nothing to do with a price.
 */
export function moved(x: number | null | undefined): "up" | "down" | "flat" {
  if (x === null || x === undefined || x === 0) return "flat";
  return x > 0 ? "up" : "down";
}
